import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import Loading from '../../../components/common/Loading'
import { useTranslation } from 'react-i18next'
import { setErrors } from '../../../../states/containers/common/errorsSlice'
import { ReactSVG } from 'react-svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import { fetchOfficeIpList, updateOfficeIpList } from '../../../../states/containers/setting/systemSlice'
import { validateIpAddress } from '../../../../utils/validate'
import { useAlert } from 'react-alert'
import { getIpAddress } from '../../../../states/apis/setting'

interface IpList {
  ip_address: string,
  comment: string,
  error: boolean,
  unique_id: string,
}

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    system,
    login,
  } = useSelector((state: RootState) => (state))

  const initIpList = {
    ip_address: "",
    comment: "",
    error: false,
    unique_id: 0,
  }

  const [ipAddress, setIpAddress] = useState<string>()
  const [ipList, setIpList] = useState<IpList[]>([])
  const [isSubmit, setIsSubmit] = useState<boolean>(true)

  // ip list
  const addIpList = () => {
    setIpList([...ipList, {
      ...initIpList,
      unique_id: uniqueId(),
    }])
  }

  const deleteIpList = (id: number) => {
    const tmpIpList = ipList.filter((_value, index) => index !== id)
    setIpList(tmpIpList)
  }

  const updateIpAddress = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const tmpIpList = [...ipList]
    if (!validateIpAddress(event.target.value) && event.target.value) {
      tmpIpList[index] = { ...tmpIpList[index], ip_address: event.target.value, error: true }
    } else {
      tmpIpList[index] = { ...tmpIpList[index], ip_address: event.target.value, error: false }
    }
    setIpList(tmpIpList)
  }

  const updateComment = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const tmpIpList = [...ipList]
    tmpIpList[index] = { ...tmpIpList[index], comment: event.target.value }
    setIpList(tmpIpList)
  }

  const handleSubmit = () => {
    dispatch(
      updateOfficeIpList(
        { list: ipList },
        () => {
          dispatch(fetchOfficeIpList())
          alert.success(t('setting.common.office_ip_address.alert.success'))
        },
        () => {
          alert.error(t('setting.common.office_ip_address.alert.error'))
        },
      ),
    )
  }

  const ipListHTML = (
    <>
      {
        ipList.length ? (
          <table className="flex-table ip_address_list_table">
            <thead>
              <tr>
                <th>{t('setting.common.office_ip_address.table.ip_address')}</th>
                <th>{t('setting.common.office_ip_address.table.memo')}</th>
                <th>{t('setting.common.office_ip_address.table.delete')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    type="text"
                    value={t('setting.common.office_ip_address.table.example.ip_address') as string}
                    readOnly
                    disabled
                    className="example"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={t('setting.common.office_ip_address.table.example.memo') as string}
                    readOnly
                    disabled
                    className="example"
                  />
                </td>
                <td />
              </tr>
              {
                ipList?.map((ip, index) => (
                  <tr key={`ip_${ip.unique_id}`}>
                    <td>
                      <input
                        type="text"
                        defaultValue={ip.ip_address}
                        onChange={(event) => updateIpAddress(event, index)}
                        className={ip.error ? "error" : ""}
                      />
                      {ip.error}
                    </td>
                    <td>
                      <input
                        type="text"
                        defaultValue={ip.comment}
                        onChange={(event) => updateComment(event, index)}
                      />
                    </td>
                    <td>
                      <ReactSVG
                        src="/static/images/icon/icon-trash.svg"
                        className="icon"
                        onClick={() => deleteIpList(index)}
                        title={t('setting.common.office_ip_address.delete_button')}
                      />
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        ) : (
          <p style={{ marginLeft: "16px" }}>
            {t('setting.common.office_ip_address.no_data')}
          </p>
        )
      }
    </>
  )

  useEffect(() => {
    if (!login.accessToken) return
    getIpAddress()
      .then((result) => {
        setIpAddress(result.ip_address)
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }, [login.accessToken])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchOfficeIpList())
  }, [login.accessToken])

  useEffect(() => {
    if (!system.office_ip_list) return
    setIpList(system.office_ip_list.map((value) => ({ ...value, error: false, unique_id: uniqueId() })))
  }, [system.office_ip_list])

  useEffect(() => {
    setIsSubmit(!ipList.find((value) => value.error === true))
  }, [ipList])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 共通設定 - ${t('setting.common.office_ip_address.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        共通設定
        &nbsp;&gt;&nbsp;
        {t('setting.common.office_ip_address.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.common.office_ip_address.title')}
        </h1>
      </section>
      <section className="page_description">
        <p>
          オフィスのIPアドレスを設定して、アクセス元によるパフォーマンスの差異を計測します。
        </p>
        <p>
          詳しい設定方法は
          <a href="https://qasee.zendesk.com/hc/ja/articles/43678378316441/" target="_blank" rel="noreferrer">こちら</a>
          をご覧ください。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="ip_restriction_table_wrapper">
              {
                system.isLoading ? (
                  <Loading />
                ) : (
                  <>
                    {ipListHTML}
                    <div role="none" onClick={addIpList} className="add">
                      <FontAwesomeIcon icon={['fas', 'plus']} style={{ marginRight: '4px' }} fixedWidth />
                      {t('setting.common.office_ip_address.add_button')}
                    </div>
                  </>
                )
              }
            </div>
            <div>
              <div
                className="ip_restriction_info"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: t('setting.common.office_ip_address.info') }}
              />
              <div style={{ margin: "16px 0" }}>
                {t('setting.common.office_ip_address.ip_address')}
                <span style={{ fontWeight: "bold" }}>
                  {ipAddress}
                </span>
              </div>
              <button type="button" className="button" onClick={handleSubmit} disabled={!isSubmit}>
                {t('setting.common.office_ip_address.setting_button')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </section>
  )
}

export default Index
